import { useEffect, useState } from "react";
import { WEIGHT_MIN, clampQty } from "./qtyUtils";

/**
 * Quantity field for an order line. For weight products it must accept a typed
 * decimal (e.g. "1.025"). A plain controlled number input can't do that —
 * coercing to a number on every keystroke drops "1.0" to "1", so a zero right
 * after the decimal point can never be typed. This keeps a local text buffer
 * while focused and re-formats only on blur.
 *
 * The parent value is updated on EVERY valid keystroke (not only on blur):
 * the save button is a react-aria press target that does NOT move focus, so a
 * blur-only commit silently loses the typed weight when the user types and
 * clicks save directly. Enter also commits, via blur().
 */
export function QtyInput({
	value,
	weight,
	onCommit,
	className = "w-12 text-center text-sm font-bold bg-white rounded-full outline-none",
}: {
	value: number;
	weight: boolean;
	onCommit: (n: number) => void;
	className?: string;
}) {
	const [text, setText] = useState(String(value));
	const [focused, setFocused] = useState(false);

	useEffect(() => {
		if (!focused) setText(String(value));
	}, [value, focused]);

	function commit() {
		const parsed = parseFloat(text);
		const fallback = weight ? WEIGHT_MIN : 1;
		const next = clampQty(Number.isFinite(parsed) ? parsed : fallback, weight);
		onCommit(next);
		setText(String(next));
	}

	return (
		<input
			type="text"
			inputMode="decimal"
			value={text}
			onClick={(e) => e.stopPropagation()}
			onFocus={() => setFocused(true)}
			onChange={(e) => {
				// Hebrew/IL keyboards often produce a comma for the decimal key.
				const raw = e.target.value.replace(",", ".");
				// Allow only digits and a single decimal point while typing.
				if (!/^\d*\.?\d*$/.test(raw)) return;
				setText(raw);
				const parsed = parseFloat(raw);
				if (Number.isFinite(parsed)) onCommit(clampQty(parsed, weight));
			}}
			onKeyDown={(e) => {
				if (e.key === "Enter") e.currentTarget.blur();
			}}
			onBlur={() => {
				setFocused(false);
				commit();
			}}
			className={className}
		/>
	);
}
